import type { FormEvent, ReactElement } from 'react';
import { useState } from 'react';
import type { FakeLeadInput } from '@/domain/entities/FakeLead';
import styles from './FakeLeadCaptureForm.module.css';

export interface FakeLeadCaptureFormProps {
  onSubmit: (input: FakeLeadInput) => void;
  isLoading: boolean;
  errorMessage: string | null;
  /** Texto de finalidade do uso dos dados (aprovado ou fallback neutro —
   * resolvido por `resolveConsentText`, HUB-201). Exibido antes dos campos
   * de captura, nunca depois. */
  consentText: string;
}

const EMPTY_INPUT: FakeLeadInput = { name: '', email: '', consentAccepted: false };

/**
 * Formulário de captura de lead fictício (nome/email/consentimento).
 * Componente de apresentação puro — toda a lógica de captura e o
 * `consent_text_version` hardcoded vivem em `CaptureFakeLeadUseCase`
 * (HUB-147).
 */
export function FakeLeadCaptureForm({
  onSubmit,
  isLoading,
  errorMessage,
  consentText,
}: FakeLeadCaptureFormProps): ReactElement {
  const [input, setInput] = useState<FakeLeadInput>(EMPTY_INPUT);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (isLoading) return;
    onSubmit(input);
  }

  return (
    <section className={styles.container} aria-label="Captura de lead">
      <form className={styles.card} onSubmit={handleSubmit} noValidate>
        <h1 className={styles.title}>Capturar lead</h1>
        <p className={styles.subtitle}>Preencha os dados do participante fictício.</p>

        <p className={styles.consentNotice}>{consentText}</p>

        <label className={styles.label} htmlFor="lead-name">
          Nome
        </label>
        <input
          id="lead-name"
          className={styles.input}
          type="text"
          autoComplete="off"
          value={input.name}
          onChange={(event) => setInput({ ...input, name: event.target.value })}
          disabled={isLoading}
        />

        <label className={styles.label} htmlFor="lead-email">
          Email
        </label>
        <input
          id="lead-email"
          className={styles.input}
          type="email"
          autoComplete="off"
          value={input.email}
          onChange={(event) => setInput({ ...input, email: event.target.value })}
          disabled={isLoading}
        />

        <label className={styles.checkboxLabel} htmlFor="lead-consent">
          <input
            id="lead-consent"
            type="checkbox"
            checked={input.consentAccepted}
            onChange={(event) => setInput({ ...input, consentAccepted: event.target.checked })}
            disabled={isLoading}
          />
          Aceito os termos de uso e consentimento de dados (LGPD).
        </label>
        <p className={styles.consentHint}>
          Você pode participar mesmo sem aceitar — seus dados não serão enviados.
        </p>

        {errorMessage && (
          <p className={styles.error} role="alert">
            {errorMessage}
          </p>
        )}

        <button type="submit" className={styles.button} disabled={isLoading}>
          {isLoading ? 'Enviando...' : 'Enviar'}
        </button>
      </form>
    </section>
  );
}
